import React from "react"
import { useTranslation } from "react-i18next"
import { StyleSheet, View } from "react-native"

import {
  AdminInfo,
  BatteryCriticalWarning,
  DeviceOfflineBadge,
  DeviceUnreachableWarning,
  LastCheckInText,
  NextScheduledActionText,
  StatusBadges,
} from "@/components/DeviceListItem"
import type { SensorState } from "@/base/sensor-events"
import type { CenterPivot } from "@/redux/center-pivots.reducer"
import type {
  DeviceStatusData,
  SensorStatusBadges,
} from "@/redux/device-event-last.reducer"
import type { UnifiedPivotData } from "@/redux/selectors/center-pivot-selectors"
import {
  AppIcons,
  AppText,
  Box,
  Button,
  COLORS,
  Collapse,
  Divider,
  Pressable,
  Row,
  SPACING,
  useIsDarkMode,
} from "@/theme"

import { CompactRow } from "./DeviceListItemCondensed"
import {
  getMostRecentCheckIn,
  getPivotRowState,
  getWorstBatteryState,
  selectPrimaryPivotBadgeKeys,
} from "./DeviceListItemCondensed.helpers"

const DOT_SIZE = 10
const ROW_VERTICAL_PADDING = SPACING.$2

export interface CenterPivotListItemCondensedProps {
  controllerStatus: DeviceStatusData | undefined
  isExpanded: boolean
  isSelected?: boolean
  monitorStatus: DeviceStatusData | undefined
  /**
   * Open the underlying device profile for the pivot. Prefers the controller
   * alias, falls back to the monitor's. When neither device is linked, the
   * caller should pass a no-op (the button is hidden in that case).
   */
  onOpenProfile: () => void
  onPressRow?: () => void
  onToggleExpanded: () => void
  pivot: CenterPivot
  showAdminInfo?: boolean
  unifiedData: UnifiedPivotData
}

/**
 * Build a merged badge set for the unified pivot row. Controller values win
 * *when defined*; otherwise the monitor's value fills in. A plain object
 * spread doesn't work here: the badge builder explicitly assigns
 * `pivotTimeToStop = undefined` on the controller (no activity lives there),
 * and that explicit undefined would shadow the monitor's real value.
 */
function mergeBadges(
  controllerBadges: SensorStatusBadges | undefined,
  monitorBadges: SensorStatusBadges | undefined,
): SensorStatusBadges {
  const result: SensorStatusBadges = { ...(monitorBadges ?? {}) }
  if (controllerBadges) {
    for (const key of Object.keys(controllerBadges) as Array<
      keyof SensorStatusBadges
    >) {
      const value = controllerBadges[key]
      if (value !== undefined) {
        // Skipping `undefined` so controller's empty slots don't shadow the
        // monitor's populated ones (pivotTimeToStop/pivotSpeed/pivotHeading
        // only live on the monitor's activity).
        ;(result as Record<string, unknown>)[key] = value
      }
    }
  }
  return result
}

function pivotSubtitle(
  unifiedData: UnifiedPivotData,
  baseLabel: string,
  controllerOnlyLabel: string,
  monitorOnlyLabel: string,
  noDevicesLabel: string,
): string {
  switch (unifiedData.deviceState.type) {
    case "controller-only":
      return `${baseLabel} · ${controllerOnlyLabel}`
    case "monitor-only":
      return `${baseLabel} · ${monitorOnlyLabel}`
    case "no-devices":
      return `${baseLabel} · ${noDevicesLabel}`
    case "full":
    case "legacy":
    default:
      return baseLabel
  }
}

/**
 * Condensed list row for a center pivot. Combines controller + monitor into a
 * single scannable row matching `DeviceListItemCondensed`'s visual contract;
 * expanding the row reveals two stacked sub-rows, one per linked device.
 */
export function CenterPivotListItemCondensed({
  controllerStatus,
  isExpanded,
  isSelected = false,
  monitorStatus,
  onOpenProfile,
  onPressRow,
  onToggleExpanded,
  pivot,
  showAdminInfo = false,
  unifiedData,
}: CenterPivotListItemCondensedProps): React.JSX.Element {
  const { t } = useTranslation("devices")
  const isDark = useIsDarkMode()
  const handlePressRow = onPressRow ?? onToggleExpanded

  const state = getPivotRowState({ controllerStatus, monitorStatus })
  const lastCheckIn = getMostRecentCheckIn(controllerStatus, monitorStatus)
  const worstBattery = getWorstBatteryState(
    controllerStatus?.batteryState,
    monitorStatus?.batteryState,
  )

  const mergedBadges = mergeBadges(
    controllerStatus?.badges,
    monitorStatus?.badges,
  )
  const primaryBadgeKeys = selectPrimaryPivotBadgeKeys({
    controllerBadges: controllerStatus?.badges,
    monitorBadges: monitorStatus?.badges,
  })

  const baseLabel = t("centerPivot", { defaultValue: "Center Pivot" })
  const controllerOnlyLabel = t("pivotControllerOnly", {
    defaultValue: "No sensor data",
  })
  const monitorOnlyLabel = t("pivotMonitorOnly", {
    defaultValue: "Monitor only",
  })
  const noDevicesLabel = t("pivotNoDevices", { defaultValue: "No devices" })
  const secondaryText = pivotSubtitle(
    unifiedData,
    baseLabel,
    controllerOnlyLabel,
    monitorOnlyLabel,
    noDevicesLabel,
  )

  const accentColor = COLORS.$buttonPrimary.light
  const rowStyle = [
    styles.row,
    isSelected
      ? [
          styles.rowSelected,
          {
            backgroundColor: isDark ? COLORS.$gray[800] : COLORS.$amber[100],
            borderLeftColor: accentColor,
          },
        ]
      : styles.rowUnselected,
  ]

  const chevronLabel = isExpanded
    ? t("collapseDeviceRow", { defaultValue: "Collapse device details" })
    : t("expandDeviceRow", { defaultValue: "Expand device details" })

  const canOpenProfile = Boolean(
    controllerStatus?.codaDeviceAlias ?? monitorStatus?.codaDeviceAlias,
  )

  const percentComplete = unifiedData.metrics.percentComplete ?? undefined

  return (
    <View style={rowStyle}>
      <Row alignItems={"center"} flexWrap={"nowrap"}>
        <Box flex={1}>
          <Pressable
            accessibilityLabel={pivot.name}
            style={styles.pressableRowBody}
            onPress={handlePressRow}
          >
            <CompactRow
              badges={mergedBadges}
              deviceEventTimestamp={lastCheckIn}
              deviceInstallationType={"center_pivot_controller"}
              deviceName={pivot.name}
              isDark={isDark}
              primaryBadgeKeysOverride={primaryBadgeKeys}
              runCompletionPct={percentComplete}
              secondaryText={secondaryText}
              state={state}
            />
          </Pressable>
        </Box>
        <Pressable
          accessibilityLabel={chevronLabel}
          accessibilityRole={"button"}
          style={styles.pressableChevron}
          onPress={onToggleExpanded}
        >
          {isExpanded ? (
            <AppIcons.ChevronUp size={20} />
          ) : (
            <AppIcons.ChevronDown size={20} />
          )}
        </Pressable>
      </Row>

      <Collapse isOpen={isExpanded}>
        <ExpandedDetail
          canOpenProfile={canOpenProfile}
          controllerStatus={controllerStatus}
          monitorStatus={monitorStatus}
          showAdminInfo={showAdminInfo}
          worstBattery={worstBattery}
          onOpenProfile={onOpenProfile}
        />
      </Collapse>
    </View>
  )
}

interface ExpandedDetailProps {
  canOpenProfile: boolean
  controllerStatus: DeviceStatusData | undefined
  monitorStatus: DeviceStatusData | undefined
  onOpenProfile: () => void
  showAdminInfo: boolean
  worstBattery: SensorState<"battery"> | null | undefined
}

function ExpandedDetail({
  canOpenProfile,
  controllerStatus,
  monitorStatus,
  onOpenProfile,
  showAdminInfo,
  worstBattery,
}: ExpandedDetailProps): React.JSX.Element {
  const { t } = useTranslation("devices")

  const controllerHeader = t("pivotControllerSubrowLabel", {
    defaultValue: "Controller",
  })
  const monitorHeader = t("pivotMonitorSubrowLabel", {
    defaultValue: "Monitor",
  })
  const controllerMissing = t("pivotControllerMissing", {
    defaultValue: "No controller linked",
  })
  const monitorMissing = t("pivotMonitorMissing", {
    defaultValue: "No sensor linked",
  })
  const profileLinkText = t("deviceProfileLinkText")

  return (
    <Box px={"$3"} py={"$2"}>
      <Divider mb={"$2"} />

      <SubDeviceSection
        data={controllerStatus}
        headerText={controllerHeader}
        missingText={controllerMissing}
        showAdminInfo={showAdminInfo}
      />

      <Box my={"$2"}>
        <Divider />
      </Box>

      <SubDeviceSection
        data={monitorStatus}
        headerText={monitorHeader}
        missingText={monitorMissing}
        showAdminInfo={showAdminInfo}
      />

      <BatteryCriticalWarning batteryState={worstBattery} mt={"$2"} />

      {controllerStatus ? (
        <NextScheduledActionText
          deviceInstallationType={controllerStatus.deviceInstallationType}
          nextScheduledAction={controllerStatus.nextScheduledAction}
        />
      ) : null}

      {canOpenProfile ? (
        <Row justifyContent={"flex-end"} mt={"$3"}>
          <Button
            IconComponent={"ListItemEnd"}
            iconPosition={"right"}
            size={"sm"}
            text={profileLinkText}
            variant={"outline"}
            onPress={onOpenProfile}
          />
        </Row>
      ) : null}
    </Box>
  )
}

interface SubDeviceSectionProps {
  data: DeviceStatusData | undefined
  headerText: string
  missingText: string
  showAdminInfo: boolean
}

function SubDeviceSection({
  data,
  headerText,
  missingText,
  showAdminInfo,
}: SubDeviceSectionProps): React.JSX.Element {
  if (!data) {
    return (
      <Box>
        <AppText
          colorScheme={"secondary"}
          fontSize={"$xs"}
          style={styles.subRowHeader}
        >
          {headerText}
        </AppText>
        <Row alignItems={"center"} space={"$1"}>
          <AppIcons.Warning color={COLORS.$amber[500]} size={DOT_SIZE + 4} />
          <AppText colorScheme={"secondary"} fontSize={"$sm"}>
            {missingText}
          </AppText>
        </Row>
      </Box>
    )
  }

  const { unreachableUntil } = data
  const isUnreachable =
    typeof unreachableUntil === "string" &&
    new Date(unreachableUntil) > new Date()

  return (
    <Box>
      <AppText
        colorScheme={"secondary"}
        fontSize={"$xs"}
        style={styles.subRowHeader}
      >
        {headerText}
      </AppText>
      <LastCheckInText
        deviceEventTimestamp={data.deviceEventTimestamp}
        fontSize={"$xs"}
      />
      {showAdminInfo ? (
        <Box mt={"$1"}>
          <AdminInfo
            deviceId={data.deviceId}
            firmwareVersion={data.firmwareVersion}
            hardwareGeneration={data.hardwareGeneration}
          />
        </Box>
      ) : null}
      {isUnreachable && typeof unreachableUntil === "string" ? (
        <DeviceUnreachableWarning
          deviceId={data.deviceId}
          my={"$2"}
          unreachableUntil={unreachableUntil}
        />
      ) : null}
      {data.deviceStatus === "offline" ? (
        <Box my={"$2"}>
          <DeviceOfflineBadge />
        </Box>
      ) : (
        <Box mt={"$2"}>
          <StatusBadges
            badges={data.badges}
            deviceInstallationType={data.deviceInstallationType}
            deviceStatus={data.deviceStatus}
          />
        </Box>
      )}
    </Box>
  )
}

const styles = StyleSheet.create({
  pressableChevron: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: SPACING.$1,
    paddingVertical: ROW_VERTICAL_PADDING,
  },
  pressableRowBody: {
    paddingLeft: SPACING.$2,
    paddingRight: SPACING.$1,
    paddingVertical: ROW_VERTICAL_PADDING,
  },
  row: {
    borderRadius: 6,
  },
  rowSelected: {
    borderLeftWidth: 3,
  },
  rowUnselected: {
    borderLeftColor: COLORS.$transparent,
    borderLeftWidth: 3,
  },
  subRowHeader: {
    fontWeight: "600",
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
})
